// Desktop sign-in: the system browser with PKCE, run by the desktop main process (F4). The renderer
// never sees a password form, token or code. Today the server's PKCE endpoints are not
// implemented, so this screen honestly shows "not set up" until they are.
import type { Session } from '@bcio-voice/contracts';
import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ApiClient, DataEnvelope } from '../lib/api.ts';
import { APP_NAME, BRAND_ASSETS } from '../config/brand.ts';
import { BrandImage } from '../ui/Wordmark.tsx';
import { Icon } from '../ui/Icon.tsx';
import { StatePanel } from '../ui/StatePanel.tsx';
import { Diagnostics } from '../ui/Diagnostics.tsx';
import type { SignOutNotice } from '../state/session.ts';
import { type DesktopAuthState, type DesktopBridge, desktopErrorCode } from './bridge.ts';

export function desktopAuthCopy(state: DesktopAuthState): { title: string; body: string } {
  switch (state.status) {
    case 'unconfigured':
      return state.reason === 'api_origin_not_configured'
        ? { title: 'This desktop app isn’t connected to a server yet', body: `Use ${APP_NAME} in your web browser for now.` }
        : { title: 'Desktop sign-in isn’t available yet', body: `Signing in through your browser isn’t set up on the server yet. Use ${APP_NAME} in your web browser for now.` };
    case 'signed_out':
      if (state.last_error === 'restore_unavailable') {
        // F4: the saved sign-in is kept and retried automatically; the app stays signed out (never
        // an anonymous workspace) until the server accepts it. No browser flow starts on its own.
        return { title: 'Reconnecting…', body: `Your sign-in on this computer is saved, but ${APP_NAME} can’t be reached right now. It will try again automatically; you don’t need to sign in again.` };
      }
      return { title: 'Sign in', body: lastErrorText(state.last_error) ?? `You sign in to ${APP_NAME} in your web browser, then come back here.` };
    case 'awaiting_browser':
      return { title: 'Continue in your browser', body: 'Finish signing in on the page that opened in your browser, then come back here.' };
    case 'exchanging':
      return { title: 'Signing you in…', body: 'Completing sign-in with the server.' };
    default:
      return { title: 'Signed in', body: 'Loading your workspace…' };
  }
}

function lastErrorText(code: string | null): string | null {
  switch (code) {
    case null:
      return null;
    case 'authorization_denied':
      return 'Sign-in was cancelled or denied in the browser. Try again when you’re ready.';
    case 'state_mismatch':
    case 'flow_expired':
    case 'no_pending_flow':
      return 'That sign-in expired or didn’t match this app. Start again.';
    case 'refresh_failed':
      return 'Your sign-in on this computer expired. Sign in again.';
    case 'browser_open_failed':
      return 'Your browser didn’t open. Try again, or check your default browser.';
    default:
      return 'The server couldn’t complete sign-in. Try again.';
  }
}

export function DesktopSignIn({ desktop, api, notice, onSignedIn, embedded, initialState = null }: { desktop: DesktopBridge; api: Pick<ApiClient, 'request'>; notice: SignOutNotice; onSignedIn: (session: Session) => void; embedded?: boolean; initialState?: DesktopAuthState | null }) {
  const [state, setState] = useState<DesktopAuthState | null>(initialState);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    desktop.auth.state().then(s => { if (live) setState(s); }, () => { if (live) setProblem('The desktop app couldn’t report its sign-in state.'); });
    const off = desktop.auth.onChange(s => { if (live) setState(s); });
    return () => { live = false; off(); };
  }, [desktop]);

  // Signed in on this computer: the session comes from the server through the desktop bridge.
  useEffect(() => {
    if (state?.status !== 'signed_in') return;
    let live = true;
    api.request<DataEnvelope<Session>>('/auth/session').then(
      env => {
        if (!live) return;
        if (env.data.user) onSignedIn(env.data);
        else setProblem('This computer signed in, but the server didn’t accept the sign-in yet.');
      },
      () => { if (live) setProblem('This computer signed in, but the server couldn’t be reached.'); },
    );
    return () => { live = false; };
  }, [state, api, onSignedIn]);

  const run = async (action: () => Promise<DesktopAuthState>) => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      setState(await action());
    } catch (error) {
      const code = desktopErrorCode(error);
      setProblem(code === 'browser_open_failed' ? 'Your browser didn’t open. Try again, or check your default browser.' : 'The desktop app couldn’t start sign-in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const body = (() => {
    if (!state) return <StatePanel variant="loading" compact title="Checking sign-in…" />;
    const copy = desktopAuthCopy(state);
    const restoring = state.status === 'signed_out' && state.last_error === 'restore_unavailable';
    if (state.status === 'unconfigured') {
      return (
        <>
          <StatePanel variant="unconfigured" compact title={copy.title} body={copy.body} />
          <Diagnostics diagnostic={{ code: state.reason, detail: null }} />
        </>
      );
    }
    return (
      <div className="auth-form">
        {notice === 'expired' && state.status === 'signed_out' && !restoring ? <p className="notice notice-warn" role="status">Your session expired. Sign in again to continue.</p> : null}
        {notice === 'policy' && state.status === 'signed_out' && !restoring ? <p className="notice notice-warn" role="status">Your access changed. Sign in again, including your verification code, to continue.</p> : null}
        {notice === 'signed_out' && state.status === 'signed_out' && !restoring ? <p className="notice" role="status">You signed out.</p> : null}
        <p className={state.status === 'signed_out' && state.last_error && !restoring ? 'form-error' : 'field-hint'} role={state.status === 'exchanging' || state.status === 'signed_in' || restoring ? 'status' : undefined}>{copy.body}</p>
        {state.status === 'awaiting_browser' ? <p className="meta">This sign-in stays open until {new Date(state.expires_at).toLocaleTimeString()}.</p> : null}
        {problem ? <p className="form-error" role="alert">{problem}</p> : null}
        <div className="form-actions">
          {state.status === 'awaiting_browser' ? (
            <>
              <button type="button" className="btn" onClick={() => void run(() => desktop.auth.cancel())} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={() => void run(() => desktop.auth.signIn())} disabled={busy}><Icon icon={ExternalLink} /> Open the browser again</button>
            </>
          ) : state.status === 'signed_out' && restoring ? (
            // Optional: a new browser sign-in replaces the saved one. Never started automatically.
            <button type="button" className="btn" onClick={() => void run(() => desktop.auth.signIn())} disabled={busy} aria-busy={busy || undefined}><Icon icon={ExternalLink} /> Sign in again in your browser instead</button>
          ) : state.status === 'signed_out' ? (
            <button type="button" className="btn btn-primary" onClick={() => void run(() => desktop.auth.signIn())} disabled={busy} aria-busy={busy || undefined}><Icon icon={ExternalLink} /> Continue in your browser</button>
          ) : null}
        </div>
      </div>
    );
  })();

  if (embedded) return body;
  return (
    <main id="main" className="auth-page">
      <div className="auth-card">
        <p className="auth-brand"><BrandImage asset={BRAND_ASSETS.lockup} height={32} /></p>
        <h1 className="auth-title">{state ? desktopAuthCopy(state).title : 'Sign in'}</h1>
        {body}
        <p className="auth-foot">Access is by invitation. Your password and verification code are entered in your browser, never in this app.</p>
      </div>
    </main>
  );
}
